import axios from 'axios';

import {
  GroupChatDTO,
  CreateGroupChatDTO,
  AddGroupMemberDTO,
  GroupMessageDTO,
  SendGroupMessageDTO,
  UserGroupChatsDTO,
} from '../models/GroupChatDTO';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';

export const groupChatApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/message/group-chat`,
  withCredentials: true,
});

installSessionInterceptors(groupChatApi);

/**
 * Create a new group chat
 */
export const createGroupChat = async (
  request: CreateGroupChatDTO
): Promise<GroupChatDTO | null> => {
  try {
    const response = await groupChatApi.post('', request);

    return response.data;
  } catch (error) {
    console.error('Error creating group chat:', error);

    return null;
  }
};

/**
 * Get all group chats for the current user
 */
export const getUserGroupChats = async (): Promise<UserGroupChatsDTO> => {
  try {
    const response = await groupChatApi.get('/list');

    return response.data;
  } catch (error) {
    // 403 is expected when group chat license feature is not enabled
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return { groupChats: [], totalCount: 0 };
    }
    console.error('Error getting user group chats:', error);

    return { groupChats: [], totalCount: 0 };
  }
};

/**
 * Get group chat details
 */
export const getGroupChat = async (chatId: string): Promise<GroupChatDTO | null> => {
  try {
    const response = await groupChatApi.get(`/${chatId}`);

    return response.data;
  } catch (error) {
    console.error('Error getting group chat:', error);

    return null;
  }
};

/**
 * Get messages in a group chat
 */
export const getGroupMessages = async (chatId: string): Promise<GroupMessageDTO[]> => {
  try {
    const response = await groupChatApi.get(`/${chatId}/messages`);

    return response.data;
  } catch (error) {
    console.error('Error getting group messages:', error);

    return [];
  }
};

/**
 * Send a message to a group chat
 */
export const sendGroupMessage = async (
  chatId: string,
  request: SendGroupMessageDTO
): Promise<GroupMessageDTO | null> => {
  try {
    const response = await groupChatApi.post(`/${chatId}/message`, request);

    return response.data;
  } catch (error) {
    console.error('Error sending group message:', error);

    return null;
  }
};

/**
 * Add a member to a group chat (creator only)
 */
export const addGroupMember = async (
  chatId: string,
  request: AddGroupMemberDTO
): Promise<GroupChatDTO | null> => {
  try {
    const response = await groupChatApi.post(`/${chatId}/add-member`, request);

    return response.data;
  } catch (error) {
    console.error('Error adding group member:', error);

    return null;
  }
};

/**
 * Remove a member from a group chat (creator only, or self-removal)
 */
export const removeGroupMember = async (chatId: string, userId: string): Promise<boolean> => {
  try {
    await groupChatApi.delete(`/${chatId}/remove-member/${userId}`);

    return true;
  } catch (error) {
    console.error('Error removing group member:', error);

    return false;
  }
};

/**
 * Leave a group chat (self-removal convenience function)
 */
export const leaveGroupChat = async (chatId: string, currentUserId: string): Promise<boolean> => {
  return removeGroupMember(chatId, currentUserId);
};
